import React, { type ReactNode, useCallback, useEffect, useRef } from 'react';

import { useAnalytics } from '../analytics/use-analytics';

/**
 * Tracks when a user is enrolled in an edition awareness experiment
 * Returns it as a react component
 */
export function FeatureExposedAnalytics({
	flagKey,
	cohort,
	growthInitiative,
	attributes,
	children,
}: {
	flagKey: string;
	cohort: string;
	/**
	 * This is usually the owning teams name e.g. `transmuters`
	 */
	growthInitiative?: string;
	attributes?: Record<string, unknown>;
	children?: ReactNode;
}) {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	useEffect(() => {
		if (hasSent.current) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'exposed',
			actionSubject: 'feature',
			attributes: {
				...attributes,
				flagKey,
				cohort,
				growthInitiative,
			},
		});

		hasSent.current = true;
	}, [sendAnalyticsEvent, flagKey, cohort, growthInitiative, attributes]);

	if (!children) {
		return null;
	}

	return <>{children}</>;
}

/**
 * Custom hook to track when a user is enrolled in an edition awareness experiment
 */
export const useFeatureExposedAnalytics = ({
	growthInitiative,
	attributes,
}: {
	/**
	 * This is usually the owning team's name e.g. `transmuters`
	 */
	growthInitiative?: string;
	attributes?: Record<string, unknown>;
}) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const hasSent = useRef(false);

	const fireExperimentExposureEvent = useCallback(
		(flagKey: string, cohort: string) => {
			if (hasSent.current) {
				return;
			}

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'exposed',
				actionSubject: 'feature',
				attributes: {
					...attributes,
					flagKey,
					cohort,
					growthInitiative,
				},
			});

			hasSent.current = true;
		},
		[sendAnalyticsEvent, growthInitiative, attributes],
	);

	return fireExperimentExposureEvent;
};
